/**
 * Single diet API module — all Kong diet endpoints and transport helpers.
 */
import { get, post } from '../../../../service/ApiService';
import { axiosDietInstance } from '../../../../configs/AxiosInstance';
import {
  cacheKey,
  getCachedGet,
  invalidateDietCachePrefix,
} from './requestCache';
import { clearRecipeDetailsCache } from '../diet-plan/recipeDetailsCache';
import { invalidateTargetPlanPrefetch } from '../diet-plan/targetPlanPrefetch';
import { formatNutrientValue } from '../nutrients/formatNutrientValue';

// --- Response envelope ---

export function dietErrorMessage(error, fallback = 'Request failed.') {
  const detail = error?.response?.data?.detail;
  return (
    (Array.isArray(detail) ? detail[0]?.msg : detail) ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export function dietFail(error, fallback = 'Request failed.') {
  return {
    success: false,
    message: dietErrorMessage(error, fallback),
    status: error?.response?.status,
  };
}

export function dietOk(data) {
  return { success: true, data };
}

async function dietGet(url, { ttlMs = 0, fallback = 'Request failed.' } = {}) {
  try {
    const key = cacheKey('GET', url, null);
    const data = await getCachedGet(
      key,
      () => get(url, { instance: axiosDietInstance }),
      ttlMs
    );
    return dietOk(data);
  } catch (error) {
    return dietFail(error, fallback);
  }
}

async function dietPost(path, payload, { invalidatePrefixes = [] } = {}) {
  try {
    const data = await post(path, payload, { instance: axiosDietInstance });
    invalidatePrefixes.forEach((prefix) => invalidateDietCachePrefix(prefix));
    return dietOk(data);
  } catch (error) {
    return dietFail(error);
  }
}

// --- Common utils ---

const COMMON_UTILS_KEY = cacheKey('GET', '/get-common-utils', null);

export async function fetchCommonDietUtils() {
  try {
    const data = await getCachedGet(COMMON_UTILS_KEY, () =>
      get('/get-common-utils', { instance: axiosDietInstance })
    );
    return dietOk(data);
  } catch (error) {
    return dietFail(error, 'Failed to fetch common utils.');
  }
}

export function invalidateCommonDietUtilsCache() {
  invalidateDietCachePrefix('GET:/get-common-utils');
}

const formatPresetNum = formatNutrientValue;

export async function fetchPresetNutrition(event_type_id, profileId) {
  try {
    const response = await get(
      `/fetch-target-nutrients?event_type_id=${event_type_id}&client_id=${profileId}`,
      { instance: axiosDietInstance }
    );
    const target = response?.data?.target_nutrient?.[0] || {};
    return {
      calories: formatPresetNum(target.calories_kcal),
      proteins: formatPresetNum(target.protein_grams),
      fats: formatPresetNum(target.fats_grams),
      fiber: formatPresetNum(target.fiber_grams),
      carbs: formatPresetNum(target.carbs_grams),
      sugars: formatPresetNum(target.sugar_grams),
    };
  } catch {
    return { calories: 0, proteins: 0, fats: 0, fiber: 0, carbs: 0, sugars: 0 };
  }
}

export function updateDietReferenceData(pathSuffix, payload) {
  return dietPost(`/diet-utils/${pathSuffix}`, payload, {
    invalidatePrefixes: ['GET:/get-common-utils'],
  });
}

// --- Recipes ---

const RECIPE_CACHE = ['GET:/recipe/'];

export async function fetchRecipes(
  event_type_id,
  limit = 500,
  offset = 0,
  search = null,
  sort_by = null,
  sort_order = 'asc',
  filters = null,
  list_only = false
) {
  if (!event_type_id) {
    return { success: false, message: 'event_type_id is required' };
  }

  let url = `/recipe/fetch-recipes?event_type_id=${encodeURIComponent(event_type_id)}&limit=${limit}&offset=${offset}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  if (sort_by) url += `&sort_by=${encodeURIComponent(sort_by)}`;
  if (sort_order) url += `&sort_order=${encodeURIComponent(sort_order)}`;
  if (filters) url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
  if (list_only) url += '&list_only=true';

  return dietGet(url, {
    ttlMs: 0,
    fallback: 'Failed to fetch recipes.',
  });
}

/** Batch full recipe/food details by ids in a single request (read; no cache invalidation). */
export async function fetchRecipesByIds(event_type_id, recipe_ids, list_only = false) {
  const ids = (recipe_ids || []).filter(Boolean);
  if (!event_type_id || ids.length === 0) {
    return { success: false, message: 'event_type_id and recipe_ids are required' };
  }
  try {
    const data = await post(
      '/recipe/fetch-recipes-by-ids',
      { event_type_id, recipe_ids: ids, list_only },
      { instance: axiosDietInstance }
    );
    // post() wraps the body as { data, status }; unwrap so the envelope matches fetchRecipes.
    return dietOk(data?.data);
  } catch (error) {
    return dietFail(error, 'Failed to fetch recipes.');
  }
}

const recipePost = async (path, payload) => {
  const result = await dietPost(path, payload, { invalidatePrefixes: RECIPE_CACHE });
  if (result.success) {
    clearRecipeDetailsCache();
  }
  return result;
};

export const modifyRecipeServingSizes = (p) => recipePost('/recipe/modify-recipe-serving-sizes', p);
export const modifyRecipeGrams = (p) => recipePost('/recipe/modify-recipe-grams', p);
export const modifyRecipes = (p) => recipePost('/recipe/modify-recipes', p);
export const modifyRecipeNames = (p) => recipePost('/recipe/modify-recipe-names', p);
export const modifyRecipeIngredients = (p) => recipePost('/recipe/modify-recipe-ingredients', p);
export const modifyRecipeNutrientComposition = (p) =>
  recipePost('/recipe/modify-recipe-nutrient-composition', p);
export const modifyRecipeCuisines = (p) => recipePost('/recipe/modify-recipe-cuisines', p);
export const modifyRecipeDietaryPreferences = (p) =>
  recipePost('/recipe/modify-recipe-dietary-preferences', p);
export const modifyRecipeDietaryTags = (p) => recipePost('/recipe/modify-recipe-dietary-tags', p);

const SWITCH_USAGE_INVALIDATE = [
  ...RECIPE_CACHE,
  'GET:/target-plan/',
  'GET:/fetch-target',
  'GET:/template/',
  'GET:/fetch-template',
  'GET:/fetch-daily-diet',
  'GET:/food_tracking',
];

export const previewSwitchRecipeServingSizeUsages = (p) =>
  dietPost('/recipe/preview-switch-recipe-serving-size-usages', p);

export const applySwitchRecipeServingSizeUsages = (p) =>
  dietPost('/recipe/apply-switch-recipe-serving-size-usages', p, {
    invalidatePrefixes: SWITCH_USAGE_INVALIDATE,
  });

// --- Templates ---

const TEMPLATE_CACHE = ['GET:/template/', 'GET:/fetch-template'];

function invalidateTemplates() {
  TEMPLATE_CACHE.forEach((prefix) => invalidateDietCachePrefix(prefix));
}

export const fetchTemplateSummaries = (event_type_id) => {
  if (!event_type_id) return Promise.resolve({ success: false, message: 'event_type_id is required' });
  return dietGet(`/fetch-template-summaries?event_type_id=${encodeURIComponent(event_type_id)}`, {
    fallback: 'Failed to fetch template summaries.',
  });
};

export const fetchTemplateDetails = (event_type_id, template_id) => {
  if (!event_type_id) return Promise.resolve({ success: false, message: 'event_type_id is required' });
  if (!template_id) return Promise.resolve({ success: false, message: 'template_id is required' });
  return dietGet(
    `/fetch-template-details?event_type_id=${encodeURIComponent(event_type_id)}&template_id=${encodeURIComponent(template_id)}`,
    { fallback: 'Failed to fetch template details.' }
  );
};

const templatePost = (path, payload) =>
  dietPost(path, payload, { invalidatePrefixes: TEMPLATE_CACHE });

export async function modifyTemplates(payload) {
  try {
    const data = await post('/template/modify-templates', payload, { instance: axiosDietInstance });
    invalidateTemplates();
    const id = data?.data?.data?.id || data?.data?.id || data?.id;
    const message = data?.data?.message || data?.message || 'Successfully modified template';
    return { success: true, id, message, rawData: data };
  } catch (error) {
    return dietFail(error, 'Failed to modify templates.');
  }
}

export const modifyTemplateMeals = (p) => templatePost('/template/modify-template-meals', p);
export const modifyTemplateMealOptions = (p) =>
  templatePost('/template/modify-template-meal-options', p);
export const modifyTemplateMealOptionFoods = (p) =>
  templatePost('/template/modify-template-meal-option-foods', p);

// --- Plans ---

const PLAN_CACHE = [
  'GET:/target-plan/',
  'GET:/fetch-target',
  'GET:/food_tracking',
  'GET:/user_plan_details',
  'GET:/fetch-daily-diet',
];

function invalidatePlans() {
  PLAN_CACHE.forEach((prefix) => invalidateDietCachePrefix(prefix));
  invalidateTargetPlanPrefetch();
}

export async function fetchDailyDietTrackingDetails(
  event_type_id,
  start_date,
  end_date,
  client_id
) {
  try {
    const url = `/fetch-daily-diet-tracking-details?event_type_id=${encodeURIComponent(event_type_id)}&start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}&client_id=${encodeURIComponent(client_id)}`;
    const data = await get(url, { instance: axiosDietInstance });
    return { success: true, data };
  } catch (error) {
    let errorMessage = 'Failed to fetch daily diet tracking details.';
    if (error.response?.status === 404) {
      errorMessage = 'No diet tracking data found for the specified parameters.';
    } else if (error.response?.data) {
      errorMessage = dietFail(error).message || errorMessage;
    }
    throw new Error(errorMessage);
  }
}

export function modifyTargetNutrients(payload) {
  return dietPost('/modify-target-nutrients', payload, { invalidatePrefixes: PLAN_CACHE });
}

export function fetchTargetPlanSummaries(
  event_type_id,
  start_date = null,
  end_date = null,
  client_id = null
) {
  if (!event_type_id) return Promise.resolve({ success: false, message: 'event_type_id is required' });
  let url = `/fetch-target-plan-summaries?event_type_id=${encodeURIComponent(event_type_id)}`;
  if (start_date) url += `&start_date=${encodeURIComponent(start_date)}`;
  if (end_date) url += `&end_date=${encodeURIComponent(end_date)}`;
  if (client_id) url += `&client_id=${encodeURIComponent(client_id)}`;
  return dietGet(url, { fallback: 'Failed to fetch target plan summaries.' });
}

export function fetchTargetPlanDetails(event_type_id, target_plan_id, client_id = null) {
  if (!event_type_id) return Promise.resolve({ success: false, message: 'event_type_id is required' });
  if (!target_plan_id) return Promise.resolve({ success: false, message: 'target_plan_id is required' });
  let url = `/fetch-target-plan-details?event_type_id=${encodeURIComponent(event_type_id)}&target_plan_id=${encodeURIComponent(target_plan_id)}`;
  if (client_id) url += `&client_id=${encodeURIComponent(client_id)}`;
  return dietGet(url, { fallback: 'Failed to fetch target plan details.' });
}

const planPost = async (path, payload) => {
  const result = await dietPost(path, payload, { invalidatePrefixes: PLAN_CACHE });
  if (result.success) invalidateTargetPlanPrefetch();
  return result;
};

export async function modifyTargetPlans(payload) {
  try {
    const data = await post('/target-plan/modify-target-plans', payload, { instance: axiosDietInstance });
    invalidatePlans();
    const id =
      data?.data?.target_plans?.[0]?.id ||
      data?.data?.data?.target_plans?.[0]?.id ||
      data?.id;
    const message = data?.message || data?.data?.message || 'Successfully modified target plans';
    return { success: true, id, message, data, rawData: data };
  } catch (error) {
    return dietFail(error, 'Failed to modify target plans.');
  }
}

export const modifyTargetPlanMeals = (p) => planPost('/target-plan/modify-target-plan-meals', p);
export const modifyTargetPlanMealOptions = (p) =>
  planPost('/target-plan/modify-target-plan-meal-options', p);
export const modifyTargetPlanMealOptionFoods = (p) =>
  planPost('/target-plan/modify-target-plan-meal-option-foods', p);
export const modifyTargetPlanDurations = (p) =>
  planPost('/target-plan/modify-target-plan-durations', p);

export function deleteTargetPlanMealOption(event_type_id, optionId, target_plan_meal_id) {
  return modifyTargetPlanMealOptions({
    event_type_id,
    meal_options: [{ operation: 'DELETE', id: optionId, target_plan_meal_id }],
  });
}
